import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  ListItemAvatar,
  List,
  Avatar,
  ListItem,
  ListItemText,
  Stack,
  useTheme,
  Typography,
} from "@mui/material";
import api from "src/store/features/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import { TargetOrderStyles } from "../../styles/TargetOrder.style";
import {
  SelectedStateType,
  setCompletedFormSection,
  setSelectedToken,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { useAppDispatch } from "src/store/store";
import { trackEvent } from "src/Amplitude";
import { TargetOrderHoldingsSkeleton } from "src/components/common/Skeletons";
import SmallArrowDropDownIcon from "src/icons/SmallArrowDropDownIcon";
import { TokenNameDisplay } from "src/components/dca/styled-components/DcaFields";
import { setBuyCurrency, useDcaSlice } from "src/store/features/dca.slice";
import {
  CloseButton,
  Header,
  HeaderAndSearch,
  SearchBar,
  SearchBarIcon,
  SearchBarWrapper,
  Wrapper,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import CloseIcon from "@mui/icons-material/Close";
import MagnifyingGlass from "src/icons/MagnifyingGlass";

type SellDropDownProps = {
  userDefaultCurrency?: any;
  code?: string;
  source?: "DCA" | null;
  trigger?: JSX.Element;
};
export const TargetOrderBuyTokenModal = ({ code, source }: SellDropDownProps) => {
  //----------------------State---------------------
  const [modal, setModal] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  //----------------------Styles---------------------
  const classes = TargetOrderStyles();
  const theme = useTheme();
  //----------------------Redux---------------------
  const dispatch = useAppDispatch();
  const {
    data: cryptoCurrencies = [],
    refetch: refetchCrypto,
    isLoading,
  } = api.useGetCryptoCurrenciesQuery();

  const treatedCryptoCurrencies = cryptoCurrencies
    .filter((el) => el.permissions?.buy === true)
    .filter((el) => el.secondarySector !== "Stablecoin")
    .filter((el) => el.name?.toLowerCase().includes(searchVal.toLocaleLowerCase()));
  const { selectedToken } = useTargetState();

  //----------------------Hooks---------------------

  useEffect(() => {
    refetchCrypto();
  }, []);

  // get buy/sell code from url params and auto select
  useEffect(() => {
    if (cryptoCurrencies[0] && source === "DCA") {
      // Set BTC as default buy currency DCA
      dispatch(setBuyCurrency("BTC"));
      dispatch(setSelectedToken({ ...cryptoCurrencies[0] }));
    }
    if (code && cryptoCurrencies) {
      const targetCurrency = cryptoCurrencies
        .filter((el) => el.permissions?.buy === true)
        .filter((el) => el.code.toLowerCase() === code.toLocaleLowerCase());

      const body = {
        icon: targetCurrency[0]?.logoUrl,
        code: targetCurrency[0]?.code,
        type: targetCurrency[0]?.type,
      };

      dispatch(setSelectedToken(body));
    }
  }, [code, cryptoCurrencies]);

  const { buyCurrency } = useDcaSlice();

  //----------------------Functions---------------------
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };
  const handleCoinSelect = (event: React.MouseEvent, selectedCurrency: SelectedStateType) => {
    const body: SelectedStateType = {
      icon: selectedCurrency.icon,
      code: selectedCurrency.code,
      available: selectedCurrency.available,
      balance: selectedCurrency.balance,
      units: selectedCurrency.units,
      price: selectedCurrency.price,
      type: selectedCurrency.type,
      value: selectedCurrency.value,
      selected: true,
    };
    if (source === "DCA") {
      dispatch(setBuyCurrency(selectedCurrency.code));
      dispatch(setSelectedToken(body));
      handleModalToggle();
      trackEvent("Selected buy currency", { token: selectedCurrency.code }, "DCA");
    } else {
      dispatch(setCompletedFormSection("tokenInput"));
      dispatch(setSelectedToken(body));
      handleModalToggle();
      trackEvent(`Target_Order_User_Buy_Token_Modal`, {
        Selected_coin: selectedCurrency.code,
      });
    }
  };
  const handleModalToggle = () => {
    setModal((prev) => !prev);
  };

  return (
    <>
      {source === "DCA" ? (
        <Stack
          direction="row"
          onClick={handleModalToggle}
          justifyContent={"space-between"}
          flex="auto"
          height="17px"
          alignItems="center">
          <Stack direction="row" gap={1}>
            <CountryOrCryptoFlag name={selectedToken.icon || selectedToken.code} size={24} />
            <TokenNameDisplay>{buyCurrency}</TokenNameDisplay>
          </Stack>
          <SmallArrowDropDownIcon color={theme.palette.mode === "light" ? undefined : "#fff"} />
        </Stack>
      ) : (
        <Button
          disableRipple
          id="token-select-btn-buy"
          sx={{
            height: "33px",
            borderRadius: "10px",
            padding: "8px",
            width: "auto",
            alignItems: "center",
            justifyContent: "normal !important",
            backgroundColor: selectedToken.code ? "#E8F6FF" : theme.palette.primary.main,
            "&:hover": { backgroundColor: " #4e7086" },
          }}
          onClick={handleModalToggle}>
          <Stack direction="row" justifyContent="center" alignItems="center">
            {selectedToken.code && (
              <Avatar
                style={{
                  backgroundColor: "transparent",
                  maxWidth: "max-content",
                }}>
                <CountryOrCryptoFlag name={selectedToken.icon || selectedToken.code} size={20} />
              </Avatar>
            )}
            <Typography
              sx={{
                color: selectedToken.code ? theme.palette.primary.main : "#fff",
                fontWeight: 600,
                lineHeight: "17px",
                fontSize: "14px",
                paddingLeft: " 5px",
                textTransform: "capitalize",
              }}>
              {selectedToken.code ? selectedToken.code : "Select Token"}
            </Typography>
            {!selectedToken.code && <ExpandMoreIcon sx={{ color: "#fff" }} />}
          </Stack>
        </Button>
      )}

      <Wrapper
        open={modal}
        onClose={handleModalToggle}
        sx={{ "& .MuiDialog-paper": { height: "fit-content" } }}>
        <HeaderAndSearch>
          {/* header */}
          <Header>
            Select Currency to Buy
            {/* close button */}
            <CloseButton onClick={handleModalToggle}>
              <CloseIcon fontSize="inherit" />
            </CloseButton>
          </Header>

          {/* searchbar */}
          <SearchBarWrapper>
            <SearchBar
              autoFocus
              placeholder="Search"
              onChange={handleSearchChange}
              value={searchVal}
            />
            <SearchBarIcon>
              <MagnifyingGlass />
            </SearchBarIcon>
          </SearchBarWrapper>
        </HeaderAndSearch>

        <Grid container>
          <List className={classes.list} id="buy-token-list">
            {isLoading && !treatedCryptoCurrencies.length && <TargetOrderHoldingsSkeleton />}
            {treatedCryptoCurrencies.map((el: any, idx: number) => (
              <Button
                disableRipple
                id={`select-buy-token-${el.code}`}
                fullWidth
                key={idx}
                onClick={(event) =>
                  handleCoinSelect(event, {
                    icon: el?.logoUrl,
                    code: el.code,
                    balance: el.available,
                    units: el.total,
                    price: el.price,
                    type: el.type,
                    available: el.available,
                    value: el.value,
                    selected: true,
                  })
                }>
                <Grid container>
                  <ListItem
                    data-code={el.code}
                    data-icon={el?.logoUrl}
                    data-name={el.name}
                    sx={{
                      fontSize: "14px",
                    }}>
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: "transparent" }}>
                        <CountryOrCryptoFlag name={el?.logoUrl || el.code} size={40} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<b>{el.code}</b>}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                      secondary={el.name}
                      sx={{ textTransform: "none" }}
                    />
                  </ListItem>
                </Grid>
              </Button>
            ))}
          </List>
        </Grid>
      </Wrapper>
    </>
  );
};
export default TargetOrderBuyTokenModal;
